// === Impor Modul ===
import React, { useState, useEffect } from "react";
import { useAuth } from "@context/AuthContext"; // Konteks autentikasi untuk user dan token
import { getFirestore, doc, getDoc } from "firebase/firestore"; // Firestore untuk mengambil data pengguna
import axios from "axios"; // Request HTTP ke fungsi Firebase
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationCircle,
  faClock,
  faMapMarkerAlt,
  faArrowUp,
  faLink,
} from "@fortawesome/free-solid-svg-icons"; // Ikon untuk UI
import { Bar } from "react-chartjs-2"; // Komponen untuk grafik batang
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"; // Komponen Chart.js

// === Inisialisasi Chart.js ===
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// === Komponen StravaStats ===
// Menampilkan statistik, aktivitas, dan grafik tren Strava pengguna
export const StravaStats = ({ stravaData: propStravaData }) => {
  // --- State ---
  const { user, getIdToken } = useAuth();
  const [activities, setActivities] = useState([]); // Daftar aktivitas terbaru
  const [stats, setStats] = useState(null); // Statistik Strava
  const [loading, setLoading] = useState(false); // Status loading
  const [progressModal, setProgressModal] = useState({ show: false, steps: [] }); // Modal progres
  const [error, setError] = useState(null); // Pesan error

  // === Fungsi: Mengambil Data Strava ===
  // Mengambil aktivitas dan statistik dari fungsi Firebase
  const fetchStravaData = async (stravaData) => {
    // Validasi data Strava dan user
    if (!stravaData || !stravaData.access_token) {
      setError("Silakan hubungkan akun Strava Anda di halaman profil.");
      return;
    }
    if (!user) {
      setError("Silakan login untuk melihat statistik Strava.");
      return;
    }

    setLoading(true);
    // Inisialisasi modal progres
    setProgressModal({
      show: true,
      steps: [
        { name: "Mengambil aktivitas baru", status: "pending" },
        { name: "Mengambil statistik", status: "pending" },
        { name: "Menyelesaikan", status: "pending" },
      ],
    });

    try {
      // Ambil token autentikasi
      const idToken = await getIdToken();

      // Ambil aktivitas
      const activitiesResult = await axios.post(
        "https://us-central1-cakrawala-jakartakreatif.cloudfunctions.net/getStravaActivities",
        {},
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      setActivities(activitiesResult.data);
      setProgressModal((prev) => ({
        ...prev,
        steps: prev.steps.map((step) =>
          step.name === "Mengambil aktivitas baru" ? { ...step, status: "complete" } : step
        ),
      }));

      // Ambil statistik
      const statsResult = await axios.post(
        "https://us-central1-cakrawala-jakartakreatif.cloudfunctions.net/getStravaStats",
        {},
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      setStats(statsResult.data);
      setProgressModal((prev) => ({
        ...prev,
        steps: prev.steps.map((step) =>
          step.name === "Mengambil statistik" ? { ...step, status: "complete" } : step
        ),
      }));

      // Selesaikan modal
      setProgressModal((prev) => ({
        ...prev,
        steps: prev.steps.map((step) =>
          step.name === "Menyelesaikan" ? { ...step, status: "complete" } : step
        ),
      }));

      // Tutup modal setelah 1 detik
      setTimeout(() => setProgressModal({ show: false, steps: [] }), 1000);
    } catch (error) {
      setError("Gagal mengambil data Strava: " + error.message);
      setProgressModal({ show: false, steps: [] });
    } finally {
      setLoading(false);
    }
  };

  // === Efek Samping ===
  // Inisialisasi data Strava saat komponen dimuat
  useEffect(() => {
    const initializeStravaData = async () => {
      // Validasi user
      if (!user) {
        setError("Silakan login untuk melihat statistik Strava.");
        return;
      }

      let stravaData = propStravaData;
      // Jika propStravaData tidak ada, ambil dari Firestore
      if (!propStravaData || !propStravaData.access_token) {
        try {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, "users", user.uid));
          const userData = userDoc.data();
          if (userData && userData.strava && userData.strava.access_token) {
            stravaData = userData.strava;
          } else {
            setError("Silakan hubungkan akun Strava Anda di halaman profil.");
            return;
          }
        } catch (err) {
          setError("Gagal mengambil data Strava: " + err.message);
          return;
        }
      }

      // Ambil data Strava
      await fetchStravaData(stravaData);
    };

    initializeStravaData();
  }, [user, propStravaData]);

  // === Render Error ===
  // Tampilkan pesan error jika ada
  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
          <FontAwesomeIcon icon={faExclamationCircle} className="text-red-500 mr-2 w-5 h-5" />
          Statistik Strava
        </h2>
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  // === Fungsi Utilitas ===
  // Menghitung metrik tambahan dari activities
  const maxDistanceActivity = activities.reduce(
    (max, activity) => (activity.distance > (max.distance || 0) ? activity : max),
    {}
  );
  const maxElevationActivity = activities.reduce(
    (max, activity) =>
      activity.total_elevation_gain > (max.total_elevation_gain || 0) ? activity : max,
    {}
  );
  const maxTimeActivity = activities.reduce(
    (max, activity) => (activity.moving_time > (max.moving_time || 0) ? activity : max),
    {}
  );

  // Menghitung aktivitas dalam 7 hari terakhir
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  const recentActivities = activities.filter(
    (activity) => new Date(activity.start_date) >= oneWeekAgo
  );

  // Menghitung jarak dan waktu maksimum YTD (tahun 2025)
  const ytdActivities = activities.filter(
    (activity) => new Date(activity.start_date).getFullYear() === 2025
  );
  const maxDistanceYTD = ytdActivities.reduce(
    (max, activity) => (activity.distance > (max.distance || 0) ? activity : max),
    {}
  );
  const maxTimeYTD = ytdActivities.reduce(
    (max, activity) => (activity.moving_time > (max.moving_time || 0) ? activity : max),
    {}
  );

  // --- Data Grafik ---
  // Menghitung jumlah aktivitas per minggu untuk 4 minggu terakhir
  const getWeekNumber = (date) => {
    const today = new Date();
    const diffInDays = Math.floor((today - new Date(date)) / (1000 * 60 * 60 * 24));
    return Math.floor(diffInDays / 7) + 1; // Minggu ke-1 adalah minggu ini
  };

  const weeklyActivityCounts = activities.reduce((acc, activity) => {
    const week = getWeekNumber(activity.start_date);
    if (week <= 4) {
      acc[week - 1] = (acc[week - 1] || 0) + 1;
    }
    return acc;
  }, Array(4).fill(0));

  // Konfigurasi data grafik
  const chartData = {
    labels: ["Minggu Ini", "Minggu Lalu", "2 Minggu Lalu", "3 Minggu Lalu"],
    datasets: [
      {
        label: "Jumlah Aktivitas",
        data: weeklyActivityCounts,
        backgroundColor: "rgba(252, 76, 2, 0.6)", // Oranye Strava
        borderColor: "#FC4C02",
        borderWidth: 1,
      },
    ],
  };

  // Opsi grafik
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Tren Aktivitas Mingguan",
        font: { size: 16, weight: "bold" },
        color: "#1F2937", // text-gray-800
      },
      tooltip: { backgroundColor: "#1F2937", titleFont: { size: 12 }, bodyFont: { size: 12 } },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: "Jumlah Aktivitas", font: { size: 12 } },
        ticks: { stepSize: 1 },
      },
      x: {
        title: { display: true, text: "Minggu", font: { size: 12 } },
      },
    },
  };

  // Format jarak ke kilometer
  const formatDistance = (meters) => (meters / 1000).toFixed(1) + " km";

  // Format waktu ke hh:mm:ss
  const formatTime = (seconds) => {
    if (!seconds) return "N/A";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // === Render UI ===
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 sm:p-4">
      {/* --- Modal Progres --- */}
      {progressModal.show && (
        <div className="fixed px-4 inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-4 w-96 sm:w-80 shadow-2xl">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Memuat Data Strava</h3>
            <div className="space-y-3">
              {progressModal.steps.map((step, index) => (
                <div key={index} className="flex items-center">
                  <span
                    className={`w-5 h-5 rounded-full mr-3 flex items-center justify-center ${
                      step.status === "complete" ? "bg-green-500 text-white" : "bg-gray-200"
                    }`}
                  >
                    {step.status === "complete" ? (
                      <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4" />
                    ) : (
                      <span className="text-gray-500 text-xs">{index + 1}</span>
                    )}
                  </span>
                  <p className="text-gray-600">{step.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* --- Judul --- */}
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-orange-600 mr-2 w-5 h-5" />
        Statistik Strava
      </h2>

      {/* --- Konten --- */}
      {loading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      ) : stats && activities.length > 0 ? (
        <>
          {/* --- Tabel Statistik --- */}
          <div className="overflow-x-auto mb-8">
            <table className="min-w-full divide-y divide-gray-200">
             
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Aktivitas Terakhir dengan Link */}
                <tr className="hover:bg-gray-50">
                  <td className=" py-4 whitespace-nowrap text-sm text-gray-800">
                    <FontAwesomeIcon icon={faLink} className="text-orange-600 mr-2 w-4 h-4" />
                    Aktivitas Terakhir
                  </td>
                  <td className=" py-4 text-sm text-gray-600">
                    {activities[0] ? (
                      <a
                        href={`https://www.strava.com/activities/${activities[0].id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-600 hover:underline"
                      >
                        {activities[0].name}
                      </a>
                    ) : (
                      "N/A"
                    )}
                  </td>
                </tr>
                {/* Jumlah Aktivitas Minggu Terakhir */}
                <tr className="hover:bg-gray-50">
                  <td className=" py-4 whitespace-nowrap text-sm text-gray-800">
                    <FontAwesomeIcon icon={faClock} className="text-orange-600 mr-2 w-4 h-4" />
                    Aktivitas Minggu Terakhir
                  </td>
                  <td className=" py-4 whitespace-nowrap text-sm text-gray-600">
                    {recentActivities.length} aktivitas
                  </td>
                </tr>
                {/* Elevasi Terakhir */}
                <tr className="hover:bg-gray-50">
                  <td className=" py-4 whitespace-nowrap text-sm text-gray-800">
                    <FontAwesomeIcon icon={faArrowUp} className="text-orange-600 mr-2 w-4 h-4" />
                    Elevasi Terakhir
                  </td>
                  <td className=" py-4 whitespace-nowrap text-sm text-gray-600">
                    {activities[0]?.total_elevation_gain?.toFixed(0) || "N/A"} m
                  </td>
                </tr>
                {/* Jarak Maksimum YTD */}
                <tr className="hover:bg-gray-50">
                  <td className=" py-4 whitespace-nowrap text-sm text-gray-800">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-orange-600 mr-2 w-4 h-4" />
                    Jarak Maksimum YTD
                  </td>
                  <td className=" py-4 text-sm text-gray-600">
                    {maxDistanceYTD.distance
                      ? `${formatDistance(maxDistanceYTD.distance)} - ${maxDistanceYTD.name}`
                      : "N/A"}
                  </td>
                </tr>
                {/* Waktu Maksimum YTD */}
                <tr className="hover:bg-gray-50">
                  <td className=" py-4 whitespace-nowrap text-sm text-gray-800">
                    <FontAwesomeIcon icon={faClock} className="text-orange-600 mr-2 w-4 h-4" />
                    Waktu Maksimum YTD
                  </td>
                  <td className=" py-4 text-sm text-gray-600">
                    {maxTimeYTD.moving_time
                      ? `${formatTime(maxTimeYTD.moving_time)} - ${maxTimeYTD.name}`
                      : "N/A"}
                  </td>
                </tr>
                {/* Jumlah Aktivitas */}
                <tr className="hover:bg-gray-50">
                  <td className=" py-4 whitespace-nowrap text-sm text-gray-800">
                    <FontAwesomeIcon icon={faClock} className="text-orange-600 mr-2 w-4 h-4" />
                    Jumlah Aktivitas
                  </td>
                  <td className=" py-4 whitespace-nowrap text-sm text-gray-600">
                    {activities.length}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* --- Grafik Tren Aktivitas --- */}
          <div className="bg-white">
            <div className="h-64 sm:h-80">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </>
      ) : (
        <p className="text-gray-500">Tidak ada data Strava tersedia.</p>
      )}
    </div>
  );
};